import { useCallback } from 'react';
import { supabase } from './supabaseClient';

// Module-level refresh lock — shared across all useApiFetch instances.
// Prevents concurrent 401 responses from each triggering their own refreshSession() call.
let refreshPromise = null;

const getRefreshedToken = () => {
    if (!refreshPromise) {
        refreshPromise = supabase.auth.refreshSession().finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
};

const logout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/login';
};

export const useApiFetch = () => {
    const apiFetch = useCallback(async (url, options = {}) => {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
            await logout();
            throw new Error('No active session');
        }

        const makeRequest = (token) =>
            fetch(url, {
                ...options,
                headers: {
                    ...options.headers,
                    Authorization: `Bearer ${token}`,
                },
            });

        let response = await makeRequest(session.access_token);

        if (response.status === 401) {
            // All concurrent 401s share a single refresh call
            const { data: { session: refreshed }, error } = await getRefreshedToken();

            if (error || !refreshed) {
                await logout();
                throw new Error('Session expired');
            }

            response = await makeRequest(refreshed.access_token);

            // If the API still rejects after a fresh token, something is wrong server-side.
            // Log out immediately rather than letting every component retry in a loop.
            if (response.status === 401) {
                await logout();
                throw new Error('Unauthorised after token refresh');
            }
        }

        return response;
    }, []);

    return apiFetch;
};
